import React, { useState } from "react";
import * as XLSX from "xlsx";
import { NotificationManager } from "react-notifications";
import {
  CreateBulkOrder,
  CreateCompressorGuardBulkOrder,
} from "../../../services/orderService";
import CommonLoader from "../../../vlp/components/common/CommonLoader";

import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
export default function NewOrderFileUploadDialog({ onClose, accountNumber }) {
  const [fileData, setFileData] = useState(null);
  const [transformedData, setTransformedData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    if (file.type === "text/csv") {
      reader.onload = (e) => {
        const csvContent = e.target.result;
        const workbook = XLSX.read(csvContent, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const contents = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        setFileData(contents);
        transformFileData(contents); // Preview the transformed data
      };
      reader.readAsBinaryString(file);
    } else if (
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const contents = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        setFileData(contents);
        transformFileData(contents); // Preview the transformed data
      };
      reader.readAsArrayBuffer(file);
    } else {
      NotificationManager.error("Unsupported file type");
    }
  };

  const handleClose = () => {
    onClose();
  };

  const createOrderButton = () => {
    if (!transformedData || transformedData.length <= 1) return null;
    return (
      <button className="button is-primary" onClick={createOrders}>
        Create Orders
      </button>
    );
  };

  async function createOrders() {
    setLoading(true);
    const orders = transformFileData(fileData, true); // Re-transform for actual order creation
    const orderNumbers = await createOrder(orders);
    const fileName = generateFileName();
    downloadOrderNumbers(orderNumbers, fileName);
    setLoading(false);
  }

  function generateFileName() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
  }

  function downloadOrderNumbers(orderNumbers, fileName) {
    const orderNumbersString = orderNumbers.toString();
    const blob = new Blob([orderNumbersString], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `orders-${fileName}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async function createOrder(orders) {
    let isCompressorGuardOrder =
      orders[0].modelID === undefined &&
      orders[0].modelYear === undefined &&
      orders[0].mfgBrand === undefined;
    try {
      if (!isCompressorGuardOrder) {
        const response = await CreateCompressorGuardBulkOrder(orders);
        NotificationManager.success(
          "Compressor Guard Orders created successfully."
        );
        handleClose();
        setLoading(false);
        return response;
      } else {
        const response = await CreateBulkOrder(orders);
        NotificationManager.success("Orders created successfully.");
        handleClose();
        setLoading(false);
        return response;
      }
    } catch (error) {
      setLoading(false);
      NotificationManager.error("Error creating orders.");
      console.error("Error creating orders:", error);
      throw error;
    }
  }

  // Transforms data for preview and order creation
  const transformFileData = (fileData, isForOrderCreation = false) => {
    const headers = fileData[0];
    const orders = [];
    const transformedRows = [];

    for (let i = 1; i < fileData.length; i++) {
      const order = {};
      let validOrder = false;
      let isKitSelected = false;
      for (let j = 0; j < headers.length; j++) {
        let key = headers[j];
        let value = fileData[i][j] !== undefined ? fileData[i][j] : "";

        if (key === "Approx. Sample Collection Time") {
          key = key.replace(".", ""); // Fix key name if needed
        }

        if (value !== null && value !== undefined) {
          validOrder = true;

          // Convert Excel date to desired format for "Sample Collection Date"
          if (key === "Sample Collection Date" && typeof value === "number") {
            value = ExcelDateToJSDate(value,isForOrderCreation); // Convert Excel date
          }

          // Convert Excel time to desired format for "Approx Sample Collection Time"
          if (
            key === "Approx Sample Collection Time" &&
            typeof value === "number"
          ) {
            value = ExcelTimeToJSTime(value); // Convert Excel time
          }

          value = String(value); // Ensure the value is a string for the order
          order[camelCase(key)] = value;
          if (
            key === "Choose Your Kit Type" &&
            value !== null &&
            value !== undefined &&
            value.trim() !== ""
          ) {
            isKitSelected = true;
          }
        }
      }

      if (validOrder && isKitSelected) {
        order["accountNumber"] = accountNumber; // Add account number
        orders.push(order); // Push the order to the list
        transformedRows.push(order);
      }
    }

    if (!isForOrderCreation) {
      setTransformedData(transformedRows); // Set transformed data for preview
    }

    return orders;
  };

  const ExcelDateToJSDate = (date,isForOrderCreation = false) => {
    const convertedDate = new Date(Math.round((date - 25569) * 864e5));
    if(isForOrderCreation){
      convertedDate.setDate(convertedDate.getDate()); // Adjust by 1 day
    }
    else{
      convertedDate.setDate(convertedDate.getDate()+1); // Adjust by 1 day
    }

    const day = String(convertedDate.getDate()).padStart(2, "0");
    const month = String(convertedDate.getMonth() + 1).padStart(2, "0");
    const year = String(convertedDate.getFullYear()); // Get the last 2 digits of the year

    return `${month}/${day}/${year}`; // MM/DD/YY format
  };

  const ExcelTimeToJSTime = (excelTime) => {
    const totalMinutes = Math.round(excelTime * 24 * 60); // Convert fraction to minutes
    const hours = Math.floor(totalMinutes / 60); // Extract hours
    const minutes = totalMinutes % 60; // Extract minutes

    // Format time as HH:MM (24-hour format)
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}`;
  };

  const camelCase = (str) => {
    return str
      .replace(/\s(.)/g, function ($1) {
        return $1.toUpperCase();
      })
      .replace(/\s/g, "")
      .replace(/^(.)/, function ($1) {
        return $1.toLowerCase();
      });
  };

  
  return (
    <Dialog open={true} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogContent sx={{ width: "90%", height: "100%", ml: "18%" }}>
        <Box sx={{ height: "100%" }}>
          {/* Title */}
          <Typography variant="h4" gutterBottom>
            Upload File
          </Typography>
  
          {/* File Input */}
          <input type="file" accept=".xlsx, .csv" onChange={handleFileChange} />
  
          {/* File Contents */}
          {fileData && (
            <Box sx={{ overflow: "auto", height: "80%", mt: 2 }}>
              <Typography variant="h5">File Contents</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    {fileData[0].map((header, index) => (
                      <TableCell key={index}>{header}</TableCell>
                    ))}
                    {transformedData && transformedData.length > 0 && (
                      <TableCell>Account Number</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transformedData?.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {Object.values(row).map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>{cell}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
  
          {/* Order Button */}
          {createOrderButton()}
        </Box>
      </DialogContent>
  
      {/* Close Button */}
      <DialogActions>
        <Button
          onClick={handleClose}
          sx={{
            backgroundColor: "green",
            position: "absolute",
            top: "10px",
            right: "10px",
            color: "white",
            "&:hover": { backgroundColor: "darkgreen" },
          }}
        >
          Close
        </Button>
      </DialogActions>
  
      {/* Loader */}
      <CommonLoader loading={loading} />
    </Dialog>
  );
  
}
